import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Wallet,
  Award,
  Plus,
  Minus,
  MapPin,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Edit2,
  X,
  History,
  Phone,
  ArrowLeft,
  Camera,
  PawPrint,
  Zap,
  AlertTriangle,
  PauseCircle,
  CheckCircle2,
  RefreshCw,
  Eye,
  Calendar,
} from "lucide-react";
import CommonButton from "../../../components/CommonButton";
import {
  useAddCustomerRewardPointsMutation,
  useCreditCustomerWalletMutation,
  useDebitCustomerWalletMutation,
  useDeductCustomerRewardPointsMutation,
  useLazyAbandonedCartByIdQuery,
  useLazyCustomerAddressesQuery,
  useLazyCustomerAddressesByIdQuery,
  useUpdateCustomerAddressesByIdMutation,
  useLazyCustomerOrdersQuery,
  useLazyCustomerPetsQuery,
  useLazyCustomerPetsByIdQuery,
  useUpdateCustomerPetsByIdMutation,
  useLazyCustomerSubscriptionsQuery,
  useLazyGetCustomersForAdminByIdQuery,
  useLazyGetCustomerWalletByIdQuery,
  useLazyUserRewardPointsQuery,
  useLazyWalletTransactionQuery,
  useLazyRewardTransactionQuery,
} from "../../../redux/actions/Admin/abandonedCartAdmin";
import {
  useLazyAdminSubscriptionsByIdQuery,
  usePauseSubscriptionMutation,
  useResumeSubscriptionMutation,
  useAdminSubscriptionRenewMutation,
  useCancelSubscriptionMutation,
} from "../../../redux/actions/Admin/dashboardAdmin";
import { useUploadFileMutation } from "../../../redux/actions/Admin/ServiceAdmin";
import { useAlert } from "../../../components/customer/AlertContext";
import { ICustomerPetRequest, ICustomerAddressRequest } from "../../../types/Request/AdminRequest";
import Autocomplete from "react-google-autocomplete";
import { GoogleMap, useJsApiLoader, MarkerF } from "@react-google-maps/api";

import {
  breedOptionsMap,
  colorOptions,
  dietaryOptions,
  dietfrequecy,
  favouriteTreatOptions,
  groomingfrequecyOptions,
  personalityOptions,
  petTypes,
  protionSize,
  sheddingfrequecyOptions,
  suggestedConditions,
  countryCodes,
} from "../../../utils/Customer/CustomerData";
import OrderDetailDrawer from "../Order Management/OrderDetailDrawer";
import { MAPS_KEY } from "../../../config/apiUrlsCustomer";

interface ICustomer360ConsoleProps {
  userId: number;
  onBack: () => void;
}

export const Customer360Console: React.FC<ICustomer360ConsoleProps> = ({ userId, onBack }) => {
  const { showConfirm } = useAlert();
  const [activeTab, setActiveTab] = useState<
    "cart" | "wallet" | "rewards" | "pets" | "orders" | "subs" | "addresses"
  >("cart");
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Active Editors & Drawers Sub-View States
  const [editingPetId, setEditingPetId] = useState<number | null>(null);
  const [addressViewMode, setAddressViewMode] = useState<"list" | "form">("list");
  const [editingAddressId, setEditingAddressId] = useState<number | null>(null);

  // Drawer Trigger States
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<number | null>(null);

  // Queries
  const [fetchProfile, { data: profile }] = useLazyGetCustomersForAdminByIdQuery();
  const [fetchWallet, { data: wallet }] = useLazyGetCustomerWalletByIdQuery();
  const [fetchWalletTxns, { data: walletTxns }] = useLazyWalletTransactionQuery();
  const [fetchRewards, { data: rewards }] = useLazyUserRewardPointsQuery();
  const [fetchRewardTxns, { data: rewardTxns }] = useLazyRewardTransactionQuery();
  const [fetchAddresses, { data: addresses }] = useLazyCustomerAddressesQuery();
  const [fetchOrders, { data: orders }] = useLazyCustomerOrdersQuery();
  const [fetchPets, { data: pets }] = useLazyCustomerPetsQuery();
  const [fetchSubs, { data: subs }] = useLazyCustomerSubscriptionsQuery();
  const [fetchCartDetails, { data: cartDetails }] = useLazyAbandonedCartByIdQuery();

  // Subscription Details Drawer Query & Mutations
  const [getSubscriptionDetails, { data: subDetails, isFetching: loadingSubDetails }] =
    useLazyAdminSubscriptionsByIdQuery();
  const [pauseSubscription, { isLoading: isPausing }] = usePauseSubscriptionMutation();
  const [resumeSubscription, { isLoading: isResuming }] = useResumeSubscriptionMutation();
  const [renewSubscription, { isLoading: isRenewing }] = useAdminSubscriptionRenewMutation();
  const [cancelSubscription, { isLoading: isCancelling }] = useCancelSubscriptionMutation();

  // Wallet & Reward Mutations
  const [creditWallet] = useCreditCustomerWalletMutation();
  const [debitWallet] = useDebitCustomerWalletMutation();
  const [addRewards] = useAddCustomerRewardPointsMutation();
  const [deductRewards] = useDeductCustomerRewardPointsMutation();

  // Form adjustment states
  const [adjustmentAmount, setAdjustmentAmount] = useState<number>(0);
  const [remarks, setRemarks] = useState<string>("");

  useEffect(() => {
    if (userId) {
      fetchProfile(userId);
      triggerTabFetch(activeTab);
    }
  }, [userId, activeTab]);

  useEffect(() => {
    if (selectedSubscriptionId !== null) {
      getSubscriptionDetails(selectedSubscriptionId);
    }
  }, [selectedSubscriptionId, getSubscriptionDetails]);

  const triggerTabFetch = (tab: typeof activeTab) => {
    switch (tab) {
      case "cart":
        return fetchCartDetails(userId);
      case "wallet":
        fetchWallet(userId);
        return fetchWalletTxns({ userId, page: 1, pageSize: 20 });
      case "rewards":
        fetchRewards(userId);
        return fetchRewardTxns({ userId, page: 1, pageSize: 20 });
      case "addresses":
        return fetchAddresses(userId);
      case "orders":
        return fetchOrders(userId);
      case "pets":
        return fetchPets(userId);
      case "subs":
        return fetchSubs(userId);
    }
  };

  const handleWalletModification = (type: "Credit" | "Debit") => {
    if (adjustmentAmount <= 0 || !remarks) {
      showConfirm({
        type: "error",
        title: "Missing Information",
        message: "Please enter a valid amount and write a brief note explaining why.",
        confirmText: "OK",
        showCancel: false,
      });
      return;
    }
    showConfirm({
      type: "success",
      title: `Confirm Wallet ${type === "Credit" ? "Add" : "Deduct"}?`,
      message: `Are you sure you want to change this customer's wallet balance by AED ${adjustmentAmount.toFixed(2)}?`,
      confirmText: "Yes, Confirm",
      showCancel: true,
      onConfirm: async () => {
        try {
          const payload = { userId, data: { userId, amount: adjustmentAmount, remarks } };
          type === "Credit" ? await creditWallet(payload).unwrap() : await debitWallet(payload).unwrap();
          setAdjustmentAmount(0);
          setRemarks("");
          triggerTabFetch("wallet");
        } catch (err: any) {
          showConfirm({
            title: "Error",
            message: err?.data?.Message || "Failed to modify wallet.",
            type: "error",
            confirmText: "OK",
          });
        }
      },
    });
  };

  const handleRewardModification = (type: "Add" | "Deduct") => {
    if (adjustmentAmount <= 0 || !remarks) {
      showConfirm({
        type: "error",
        title: "Missing Information",
        message: "Please enter a valid amount of points and write a brief reason.",
        confirmText: "OK",
        showCancel: false,
      });
      return;
    }
    showConfirm({
      type: "success",
      title: `Confirm Points ${type === "Add" ? "Addition" : "Deduction"}`,
      message: `Are you sure you want to change this customer's reward balance by ${adjustmentAmount} points?`,
      confirmText: "Confirm",
      showCancel: true,
      onConfirm: async () => {
        try {
          const payload = { userId, data: { userId, points: adjustmentAmount, remarks } };
          type === "Add" ? await addRewards(payload).unwrap() : await deductRewards(payload).unwrap();
          setAdjustmentAmount(0);
          setRemarks("");
          triggerTabFetch("rewards");
        } catch (err: any) {
          showConfirm({
            title: "Error",
            message: err?.data?.Message || "Failed to modify reward points.",
            type: "error",
            confirmText: "OK",
          });
        }
      },
    });
  };

  // Subscription Actions
  const handleStatusChange = async (id: number, targetStatus: "pause" | "continue") => {
    try {
      if (targetStatus === "pause") {
        await pauseSubscription(id).unwrap();
      } else {
        await resumeSubscription(id).unwrap();
      }
      getSubscriptionDetails(id);
      fetchSubs(userId);
    } catch {
      showConfirm({
        title: "Error",
        message: "Failed to update subscription status.",
        type: "error",
        confirmText: "OK",
      });
    }
  };

  const handleRenewNow = async (id: number) => {
    try {
      await renewSubscription(id).unwrap();
      showConfirm({
        title: "Success",
        message: "Subscription renewed successfully!",
        type: "success",
        confirmText: "OK",
      });
      getSubscriptionDetails(id);
      fetchSubs(userId);
    } catch (error: any) {
      showConfirm({
        title: "Error",
        message: error?.data?.Message || "Failed to manually renew subscription.",
        type: "error",
        confirmText: "OK",
      });
    }
  };

  const handleCancelSubscription = async (id: number) => {
    try {
      await cancelSubscription(id).unwrap();
      showConfirm({
        title: "Cancelled",
        message: "Subscription cancelled successfully.",
        type: "success",
        confirmText: "OK",
      });
      getSubscriptionDetails(id);
      fetchSubs(userId);
    } catch (error: any) {
      showConfirm({
        title: "Error",
        message: error?.data?.Message || "Failed to cancel subscription.",
        type: "error",
        confirmText: "OK",
      });
    }
  };

  const isMutatingSub = isPausing || isResuming || isRenewing || isCancelling;

  const handleScroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -200 : 200,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="p-6 sm:p-8 flex flex-col flex-1 text-left h-full bg-white relative">
      <div className="mb-4">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs font-bold text-mainBrandColor bg-accent/10 hover:bg-accent/20 px-3 py-1.5 rounded-xl transition"
        >
          <ChevronLeft className="w-4 h-4" /> Back to Customer List
        </button>
      </div>

      {/* Header Profile Summary */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-gray-100 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-[#f9f9f9] border border-accent/30 text-mainBrandColor font-black text-xl flex items-center justify-center shadow-inner shrink-0">
            {profile?.data?.customerName?.charAt(0) || "C"}
          </div>
          <div>
            <h3 className="text-xl font-black text-logoHeading">{profile?.data?.customerName}</h3>
            <p className="text-sm text-iconColor font-medium mt-0.5">
              {profile?.data?.email || profile?.data?.phoneNumber}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 bg-[#f9f9f9] border border-slate-200 px-4 py-2 rounded-xl text-xs font-bold text-logoHeading self-start sm:self-auto">
          <span className={`w-2 h-2 rounded-full ${profile?.data?.isActive ? "bg-emerald-500" : "bg-rose-500"}`} />
          {profile?.data?.isActive ? "Active Account" : "Suspended"}
        </div>
      </div>

      {/* Navigation Pills */}
      <div className="mb-6 border-b border-gray-100 flex items-center relative group">
        <button
          onClick={() => handleScroll("left")}
          className="p-1.5 mr-1 bg-white hover:bg-gray-100 rounded-full border border-gray-200 text-iconColor shadow-sm transition hover:text-logoHeading shrink-0"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <div
          ref={scrollContainerRef}
          className="flex items-center gap-2 overflow-x-auto whitespace-nowrap pb-3 flex-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {(
            [
              { id: "cart", label: "Left Items in Cart" },
              { id: "wallet", label: "Wallet Adjustments" },
              { id: "rewards", label: "PetLand Points" },
              { id: "pets", label: "Registered Pets" },
              { id: "orders", label: "Order History" },
              { id: "subs", label: "Active Subscriptions" },
              { id: "addresses", label: "Saved Addresses" },
            ] as const
          ).map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setEditingPetId(null);
                setAddressViewMode("list");
                setEditingAddressId(null);
                setSelectedOrderId(null);
                setSelectedSubscriptionId(null);
                setAdjustmentAmount(0);
                setRemarks("");
              }}
              className={`shrink-0 rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-200 ${
                activeTab === tab.id
                  ? "bg-mainBrandColor text-white shadow-md"
                  : "bg-white text-iconColor hover:bg-gray-100 hover:text-logoHeading"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <button
          onClick={() => handleScroll("right")}
          className="p-1.5 ml-1 bg-white hover:bg-gray-100 rounded-full border border-gray-200 text-iconColor shadow-sm transition hover:text-logoHeading shrink-0"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Main Container Body */}
      <div className="flex-1 overflow-y-auto min-h-[400px] pr-1">
        {/* TAB 1: CART DETAILS */}
        {activeTab === "cart" && (
          <div className="space-y-3">
            {cartDetails?.data ? (
              <>
                <div className="bg-[#f9f9f9] border border-accent/20 rounded-xl p-4 flex justify-between items-center text-xs font-bold shadow-sm">
                  <span className="text-iconColor">Total Cart Value</span>
                  <span className="text-base font-black text-mainBrandColor">
                    AED {(cartDetails.data.totalValue || 0).toFixed(2)}
                  </span>
                </div>
                {cartDetails.data.products?.map((p) => (
                  <div key={p.productVariantId} className="border border-gray-100 p-4 rounded-xl flex justify-between items-center text-xs bg-white shadow-sm">
                    <div>
                      <h4 className="font-bold text-logoHeading">{p.productName}</h4>
                      <p className="text-iconColor mt-0.5">Quantity: {p.quantity} × AED {p.unitPrice}</p>
                    </div>
                    <span className="font-bold text-logoHeading">AED {p.totalPrice}</span>
                  </div>
                ))}
              </>
            ) : (
              <p className="text-xs text-iconColor text-center p-4">No unpurchased items left in this customer's cart.</p>
            )}
          </div>
        )}

        {/* TAB 2: WALLET */}
        {activeTab === "wallet" && wallet?.data && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-[#f9f9f9] border border-accent/20 p-4 rounded-xl text-center shadow-sm">
                <Wallet className="w-4 h-4 text-logoHeading mx-auto mb-1" />
                <span className="text-[10px] font-bold text-iconColor block">Current Balance</span>
                <p className="text-sm font-black text-logoHeading">AED {wallet.data.balance}</p>
              </div>
              <div className="bg-[#f9f9f9] border border-accent/20 p-4 rounded-xl text-center shadow-sm">
                <Plus className="w-4 h-4 text-emerald-600 mx-auto mb-1" />
                <span className="text-[10px] font-bold text-iconColor block">Total Added</span>
                <p className="text-sm font-black text-emerald-600">AED {wallet.data.lifetimeCredited}</p>
              </div>
              <div className="bg-[#f9f9f9] border border-accent/20 p-4 rounded-xl text-center shadow-sm">
                <Minus className="w-4 h-4 text-rose-600 mx-auto mb-1" />
                <span className="text-[10px] font-bold text-iconColor block">Total Used</span>
                <p className="text-sm font-black text-rose-600">AED {wallet.data.lifetimeDebited}</p>
              </div>
            </div>

            <div className="bg-[#f9f9f9] border border-accent/20 p-4 rounded-2xl shadow-inner mt-4">
              <h4 className="text-xs font-bold text-logoHeading mb-3">Adjust Wallet Balance</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <input
                  type="number"
                  placeholder="Enter Amount (AED)"
                  value={adjustmentAmount || ""}
                  onChange={(e) => setAdjustmentAmount(Number(e.target.value))}
                  className="px-3 py-2 border border-slate-200 bg-white rounded-xl text-xs outline-none"
                />
                <input
                  type="text"
                  placeholder="Reason or brief notes..."
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className="px-3 py-2 border border-slate-200 bg-white rounded-xl text-xs outline-none"
                />
              </div>
              <div className="flex justify-end gap-2">
                <CommonButton text="Deduct Money" variant="outline" className="border-red-200 text-red-600 font-bold text-xs bg-white" onClick={() => handleWalletModification("Debit")} />
                <CommonButton text="Add Money" variant="filled" className="bg-mainBrandColor font-bold text-xs text-white" onClick={() => handleWalletModification("Credit")} />
              </div>
            </div>

            <div className="mt-6">
              <h4 className="text-xs font-bold text-logoHeading mb-3 flex items-center gap-1.5"><History className="w-4 h-4 text-mainBrandColor" /> Recent Wallet Activity</h4>
              <div className="space-y-2">
                {walletTxns?.data?.data && walletTxns.data.data.length > 0 ? (
                  walletTxns.data.data.map((tx) => (
                    <div key={tx.transactionId} className="border border-gray-100 p-3 rounded-xl flex justify-between items-center text-xs bg-white shadow-sm">
                      <div>
                        <span className="font-bold text-logoHeading block">{tx.transactionType} ({tx.referenceType})</span>
                        <span className="text-[10px] text-iconColor">{tx.remarks || "No remarks"} • {new Date(tx.createdOn).toLocaleString()}</span>
                      </div>
                      <div className="text-right">
                        <span className={`font-black block ${tx.transactionType === "Credit" ? "text-emerald-600" : "text-rose-600"}`}>
                          {tx.transactionType === "Credit" ? "+" : "-"} AED {tx.amount}
                        </span>
                        <span className="text-[9px] text-iconColor">Bal: AED {tx.balanceAfter}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-iconColor text-center p-3 border border-dashed rounded-xl">No wallet transactions recorded.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: REWARDS */}
        {activeTab === "rewards" && rewards?.data && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-[#f9f9f9] border border-accent/20 p-3 rounded-xl text-center shadow-sm">
                <Award className="w-4 h-4 text-mainBrandColor mx-auto mb-1" />
                <span className="text-[10px] font-bold text-iconColor block">Available Petland Points</span>
                <p className="text-xs font-black text-logoHeading">{rewards.data.availablePoints || 0} Points</p>
              </div>
              <div className="bg-[#f9f9f9] border border-accent/20 p-3 rounded-xl text-center shadow-sm">
                <Sparkles className="w-4 h-4 text-logoHeading mx-auto mb-1" />
                <span className="text-[10px] font-bold text-iconColor block">Total point changes</span>
                <p className="text-xs font-black text-logoHeading">{rewards.data.totalTransactions || 0} times</p>
              </div>
            </div>

            <div className="bg-[#f9f9f9] border border-accent/20 p-4 rounded-2xl shadow-inner">
              <h4 className="text-xs font-bold text-logoHeading mb-3">Adjust Rewards Points</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <input
                  type="number"
                  placeholder="Enter amount of points"
                  value={adjustmentAmount || ""}
                  onChange={(e) => setAdjustmentAmount(Number(e.target.value))}
                  className="px-3 py-2 border border-slate-200 bg-white rounded-xl text-xs outline-none"
                />
                <input
                  type="text"
                  placeholder="Reason or brief notes..."
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className="px-3 py-2 border border-slate-200 bg-white rounded-xl text-xs outline-none"
                />
              </div>
              <div className="flex justify-end gap-2">
                <CommonButton text="Remove Points" variant="outline" className="border-slate-200 text-slate-700 font-bold text-xs bg-white" onClick={() => handleRewardModification("Deduct")} />
                <CommonButton text="Give Points" variant="filled" className="bg-mainBrandColor font-bold text-xs text-white" onClick={() => handleRewardModification("Add")} />
              </div>
            </div>

            <div className="mt-6">
              <h4 className="text-xs font-bold text-logoHeading mb-3 flex items-center gap-1.5"><History className="w-4 h-4 text-mainBrandColor" /> Petland point Transactions History</h4>
              <div className="space-y-2">
                {rewardTxns?.data?.data && rewardTxns.data.data.length > 0 ? (
                  rewardTxns.data.data.map((rt) => (
                    <div key={rt.transactionId} className="border border-gray-100 p-3 rounded-xl flex justify-between items-center text-xs bg-white shadow-sm">
                      <div>
                        <span className="font-bold text-logoHeading block">{rt.transactionType}</span>
                        <span className="text-[10px] text-iconColor">{rt.remarks || "No remarks"} • {new Date(rt.createdOn).toLocaleString()}</span>
                      </div>
                      <div className="text-right">
                        <span className={`font-black block ${rt.transactionType === "Earn" ? "text-emerald-600" : "text-amber-600"}`}>
                          {rt.transactionType === "Earn" ? "+" : "-"} {rt.points} pts
                        </span>
                        <span className="text-[9px] text-iconColor">Bal: {rt.balanceAfter} pts</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-iconColor text-center p-3 border border-dashed rounded-xl">No petland points logs found.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: PETS */}
        {activeTab === "pets" && (
          <div>
            {editingPetId ? (
              <div>
                <button
                  onClick={() => setEditingPetId(null)}
                  className="mb-4 flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-mainBrandColor transition"
                >
                  <ArrowLeft className="w-4 h-4" /> Cancel & Return to Registered Pets
                </button>
                <FullSingleScreenPetEditor
                  userId={userId}
                  petId={editingPetId}
                  onSuccess={() => {
                    setEditingPetId(null);
                    fetchPets(userId);
                  }}
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {pets?.data && pets.data.length > 0 ? (
                  pets.data.map((p: any) => (
                    <div key={p.petId} className="border border-gray-100 p-3.5 rounded-xl flex items-center justify-between bg-white shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center font-bold text-logoHeading text-sm shrink-0 overflow-hidden">
                          {p.photoUrl ? (
                            <img src={p.photoUrl} alt={p.petName} className="w-full h-full object-cover" />
                          ) : (
                            "🐾"
                          )}
                        </div>
                        <div className="text-xs min-w-0">
                          <h5 className="font-bold text-logoHeading truncate">{p.petName}</h5>
                          <p className="text-iconColor truncate font-medium">{p.petType} — {p.breed}</p>
                          <p className="text-[10px] font-extrabold text-mainBrandColor mt-0.5">{p.weight} kg</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setEditingPetId(p.petId)}
                        className="p-2 text-iconColor hover:text-mainBrandColor bg-slate-50 hover:bg-slate-100 rounded-lg transition"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-iconColor p-4 text-center col-span-2">This customer has no registered pets.</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* TAB 5: ORDERS (CLICK TO OPEN ORDER DETAIL DRAWER) */}
        {activeTab === "orders" && (
          <div className="space-y-2.5">
            {orders?.data && orders.data.length > 0 ? (
              orders.data.map((o: any) => (
                <div
                  key={o.orderId}
                  onClick={() => setSelectedOrderId(o.orderId)}
                  className="border border-gray-100 p-3.5 rounded-xl text-xs flex justify-between items-center bg-white shadow-sm hover:border-[#6DBE45] transition-all cursor-pointer group"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-logoHeading group-hover:text-mainBrandColor transition-colors">
                        {o.orderNumber}
                      </span>
                      <span className="text-[10px] text-iconColor">ID #{o.orderId}</span>
                    </div>
                    <span className="text-iconColor text-[10px] font-medium block mt-0.5">
                      {new Date(o.createdOn).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <span className="font-black block text-logoHeading">AED {(o.totalAmount || 0).toFixed(2)}</span>
                      <span className="text-[9px] font-extrabold px-2 py-0.5 rounded bg-[#f9f9f9] text-logoHeading border border-accent/20 inline-block mt-0.5">
                        {o.orderStatus}
                      </span>
                    </div>
                    <div className="p-2 rounded-xl bg-slate-50 text-slate-400 group-hover:bg-[#5FB63F] group-hover:text-white transition-all">
                      <Eye size={14} />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-xs text-iconColor p-4 text-center">No past orders found for this account.</p>
            )}
          </div>
        )}

        {/* TAB 6: SUBSCRIPTIONS (CLICK TO OPEN SUBSCRIPTION DETAIL DRAWER) */}
        {activeTab === "subs" && (
          <div className="space-y-2.5">
            {subs?.data && subs.data.length > 0 ? (
              subs.data.map((s: any) => (
                <div
                  key={s.subscriptionId}
                  onClick={() => setSelectedSubscriptionId(s.subscriptionId)}
                  className="border border-gray-100 p-3.5 rounded-xl text-xs flex justify-between items-center bg-white shadow-sm hover:border-[#2F5E2E] transition-all cursor-pointer group"
                >
                  <div className="flex items-center gap-3">
                    {s.imageUrl && (
                      <img src={s.imageUrl} alt="" className="w-10 h-10 object-cover rounded-lg border shrink-0" />
                    )}
                    <div>
                      <h5 className="font-bold text-logoHeading group-hover:text-mainBrandColor transition-colors">
                        {s.productName}
                      </h5>
                      <p className="text-iconColor text-[10px] font-medium mt-0.5">
                        {s.billingCycle} | Qty: {s.quantity} | Next: {s.nextBillingDate ? new Date(s.nextBillingDate).toLocaleDateString() : "—"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <span className="font-black text-mainBrandColor block">
                        AED {(s.subscriptionPrice || 0).toFixed(2)}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${
                        s.status === "Active" ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-amber-50 text-amber-700 border border-amber-100"
                      }`}>
                        {s.status}
                      </span>
                    </div>
                    <div className="p-2 rounded-xl bg-slate-50 text-slate-400 group-hover:bg-[#2F5E2E] group-hover:text-white transition-all">
                      <Eye size={14} />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-xs text-iconColor p-4 text-center">No active automated recurring plans found.</p>
            )}
          </div>
        )}

        {/* TAB 7: ADDRESSES */}
        {activeTab === "addresses" && (
          <div>
            {addressViewMode === "list" ? (
              <div className="space-y-2.5">
                {addresses?.data && addresses.data.length > 0 ? (
                  addresses.data.map((addr: any) => (
                    <div key={addr.addressId} className="border border-gray-100 p-3.5 rounded-xl text-xs bg-white shadow-sm flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 font-bold text-logoHeading mb-1">
                          <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-mainBrandColor" /> {addr.fullName || "Address"} ({addr.addressType})</span>
                          {addr.isDefault && <span className="text-[9px] bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-lg text-amber-700 font-bold">Primary</span>}
                        </div>
                        <p className="text-iconColor font-medium mt-1">{addr.addressLine1}, {addr.city}, {addr.state}</p>
                      </div>
                      <button
                        onClick={() => {
                          setEditingAddressId(addr.addressId);
                          setAddressViewMode("form");
                        }}
                        className="p-2 text-iconColor hover:text-mainBrandColor bg-slate-50 hover:bg-slate-100 rounded-lg transition"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-iconColor p-4 text-center">No delivery addresses saved yet.</p>
                )}
              </div>
            ) : (
              <AddressEditorForm
                userId={userId}
                addressId={editingAddressId}
                onCancel={() => {
                  setAddressViewMode("list");
                  setEditingAddressId(null);
                }}
                onSuccess={() => {
                  setAddressViewMode("list");
                  setEditingAddressId(null);
                  fetchAddresses(userId);
                }}
              />
            )}
          </div>
        )}
      </div>

      {/* --- ORDER DETAILS DRAWER LAYER --- */}
      {selectedOrderId !== null && (
        <OrderDetailDrawer
          orderId={selectedOrderId}
          onClose={() => setSelectedOrderId(null)}
          onRefreshDashboard={() => fetchOrders(userId)}
        />
      )}

      {/* --- SUBSCRIPTION DETAILS DRAWER LAYER --- */}
      {selectedSubscriptionId !== null && (
        <div className="fixed inset-0 bg-black/40 z-[999] flex justify-end animate-in fade-in duration-200">
          <div className="bg-white w-full sm:max-w-md h-full fixed top-0 right-0 bottom-0 shadow-2xl flex flex-col justify-between overflow-hidden z-[1000]">
            {loadingSubDetails ? (
              <div className="h-full flex items-center justify-center text-xs text-gray-500">
                <RefreshCw className="w-5 h-5 animate-spin mr-2 text-[#2F5E2E]" /> Loading details...
              </div>
            ) : subDetails ? (
              <div className="flex flex-col h-full overflow-hidden text-left text-xs">
                {/* Header */}
                <div className="p-4 sm:p-5 flex justify-between items-center border-b shrink-0 bg-white">
                  <div>
                    <h2 className="text-sm sm:text-base font-black text-gray-900">Manage Subscription</h2>
                    <p className="text-gray-400 text-[10px]">Ref: #{subDetails.orderNumber}</p>
                  </div>
                  <button
                    onClick={() => setSelectedSubscriptionId(null)}
                    disabled={isMutatingSub}
                    className="p-2 hover:bg-gray-100 rounded-lg transition cursor-pointer"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>

                {/* Body Area */}
                <div className="p-4 sm:p-5 flex-1 overflow-y-auto space-y-4 min-h-0">
                  {/* Status Control Box */}
                  <div className="bg-gray-50 border border-gray-200 p-3.5 rounded-xl space-y-3">
                    <div className="flex justify-between items-center w-full">
                      <span className="font-bold text-gray-700 text-[10px] uppercase">Controls</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${
                        subDetails.status === "Active" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                      }`}>
                        {subDetails.statusName || subDetails.status}
                      </span>
                    </div>

                    {subDetails.status === "Active" ? (
                      <button
                        onClick={() => handleStatusChange(subDetails.subscriptionId, "pause")}
                        disabled={isMutatingSub}
                        className="w-full py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-bold flex items-center justify-center gap-2 transition text-xs shadow-xs"
                      >
                        {isPausing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <PauseCircle className="w-4 h-4" />} Pause Subscription
                      </button>
                    ) : subDetails.status === "Paused" ? (
                      <button
                        onClick={() => handleStatusChange(subDetails.subscriptionId, "continue")}
                        disabled={isMutatingSub}
                        className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold flex items-center justify-center gap-2 transition text-xs shadow-xs"
                      >
                        {isResuming ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Resume Subscription
                      </button>
                    ) : (
                      <div className="text-center p-2 bg-gray-100 text-gray-500 border rounded-lg font-bold text-[11px]">
                        No active transitions available.
                      </div>
                    )}
                  </div>

                  {/* Product Specification Card */}
                  <div className="border p-3 rounded-xl flex gap-3 bg-white shadow-2xs">
                    {subDetails.imageUrl && (
                      <img src={subDetails.imageUrl} alt="" className="w-12 h-12 object-cover rounded border shrink-0 bg-gray-50" />
                    )}
                    <div className="min-w-0">
                      <h4 className="font-bold text-gray-900 line-clamp-2 text-xs leading-tight">{subDetails.productName}</h4>
                      <p className="text-gray-400 mt-0.5 text-[10px] truncate">{subDetails.variantName} | Qty: {subDetails.quantity}</p>
                      <p className="font-bold text-[#2F5E2E] mt-1 text-xs">AED {subDetails.estimatedRenewalAmount?.toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Summary Metrics */}
                  <div className="border rounded-xl p-3 space-y-2 bg-[#FAFBF8]">
                    <div className="flex justify-between"><span>Frequency Cycle:</span> <strong className="text-gray-900">{subDetails.billingCycleName || "Custom"}</strong></div>
                    <div className="flex justify-between"><span>Lifetime Revenue:</span> <strong className="text-emerald-700">AED {subDetails.lifetimeRevenue?.toFixed(2)}</strong></div>
                    <div className="flex justify-between"><span>Next Invoice Run:</span> <span className="font-bold text-gray-800">{new Date(subDetails.nextBillingDate).toLocaleDateString()}</span></div>
                  </div>

                  {/* Renewal Log History */}
                  <div className="space-y-1.5">
                    <p className="text-gray-400 font-bold text-[10px] uppercase">Renewal History ({subDetails.renewals?.length || 0})</p>
                    <div className="border rounded-xl max-h-40 overflow-y-auto divide-y bg-white">
                      {subDetails.renewals?.map((r) => (
                        <div key={r.renewalId} className="p-2.5 flex justify-between items-center text-[10px]">
                          <div className="min-w-0 pr-2">
                            <p className="font-bold text-gray-800 truncate">Order #{r.orderId}</p>
                            <p className="text-gray-400 text-[9px]">{new Date(r.billingDate).toLocaleDateString()}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <span className="font-bold text-gray-900 block">AED {r.amount}</span>
                            <span className={`text-[9px] px-1 rounded font-bold inline-block ${r.paymentSucceeded ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>{r.statusName}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Sticky Bottom Actions */}
                {subDetails.status !== "Cancelled" && (
                  <div className="p-4 border-t bg-slate-50/90 shrink-0 space-y-2">
                    <button
                      onClick={() => handleRenewNow(subDetails.subscriptionId)}
                      disabled={isMutatingSub}
                      className="w-full py-2.5 bg-[#2F5E2E] hover:bg-[#234722] text-white text-xs font-bold rounded-xl flex items-center justify-center gap-2 transition disabled:opacity-50"
                    >
                      {isRenewing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-3.5 h-3.5" />} Renew Plan Now
                    </button>
                    <button
                      onClick={() => handleCancelSubscription(subDetails.subscriptionId)}
                      disabled={isMutatingSub}
                      className="w-full py-2.5 bg-white border border-rose-200 text-rose-600 hover:bg-rose-50 text-xs font-bold rounded-xl flex items-center justify-center gap-2 transition disabled:opacity-50"
                    >
                      {isCancelling ? <RefreshCw className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5" />} Terminate Subscription
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

// =========================================================================
// SUB-COMPONENT: UNIFIED SINGLE-SCREEN PET EDITOR FORM (ALL FIELDS AT ONCE)
// =========================================================================

const FullSingleScreenPetEditor: React.FC<{
  userId: number;
  petId: number;
  onSuccess: () => void;
}> = ({ userId, petId, onSuccess }) => {
  const { showConfirm } = useAlert();
  const [fetchPet, { data: petResponse, isFetching }] = useLazyCustomerPetsByIdQuery();
  const [updatePet, { isLoading: isUpdating }] = useUpdateCustomerPetsByIdMutation();
  const [uploadFile] = useUploadFileMutation();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [petImage, setPetImage] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState("cat");
  const [genderInput, setGenderInput] = useState("Male");
  const [petName, setPetName] = useState("");
  const [weight, setWeight] = useState<number>(0);
  const [dateType, setDateType] = useState<string>("birthday");
  const [selectedDate, setSelectedDate] = useState("");
  const [activity, setActivity] = useState("2");
  const [openDropdown, setOpenDropdown] = useState<"breed" | "color" | null>(null);
  const [breedInput, setBreedInput] = useState("");
  const [colorInput, setColorInput] = useState("");

  const [localPersonalities, setLocalPersonalities] = useState(personalityOptions);
  const [personality, setPersonality] = useState<string[]>([]);
  const [customPersonalityInput, setCustomPersonalityInput] = useState("");

  const [localDietaryOptions, setLocalDietaryOptions] = useState(dietaryOptions);
  const [dietary, setDietary] = useState<string[]>([]);
  const [customDietaryInput, setCustomDietaryInput] = useState("");

  const [localTreatOptions, setLocalTreatOptions] = useState(favouriteTreatOptions);
  const [favouriteTreats, setFavouriteTreats] = useState<string[]>([]);
  const [customTreatInput, setCustomTreatInput] = useState("");

  const [dietFrequency, setDietFrequency] = useState("2");
  const [portionSize, setPortionSize] = useState("1");

  const [localHealthConditions, setLocalHealthConditions] = useState<Array<{ id: string; label: string }>>(
    suggestedConditions.map((item) => ({ id: item.id, label: item.label }))
  );
  const [healthSearch, setHealthSearch] = useState("");
  const [healthConditions, setHealthConditions] = useState<string[]>([]);
  const [noIssues, setNoIssues] = useState(false);

  const [groomingfrequecy, setGroomingfrequecy] = useState("2");
  const [customGroomingMonths, setCustomGroomingMonths] = useState("1");
  const [sheddingfrequecy, setSheddingfrequecy] = useState("2");
  const [breedSize, setBreedSize] = useState<"Small" | "Medium" | "Large" | "Extra Large">("Medium");

  useEffect(() => {
    if (userId && petId) {
      fetchPet({ userId, petId });
    }
  }, [userId, petId]);

  useEffect(() => {
    if (petResponse?.data) {
      const data = petResponse.data;
      setPetName(data.petName || "");
      setSelectedType(data.petType?.toLowerCase() || "cat");
      setGenderInput(data.gender || "Male");
      setWeight(data.weight || 0);
      setDateType(data.dateType || "birthday");
      setSelectedDate(data.petDate ? data.petDate.split("T")[0] : "");
      setBreedInput(data.breed || "");
      setColorInput(data.furColor || "");
      setActivity(data.activityLevel || "2");
      setPetImage(data.photoUrl || null);

      if (data.personality) {
        const list = typeof data.personality === "string" ? data.personality.split(",") : data.personality;
        setPersonality(list);
      }
      if (data.foodPreferences) {
        const list = typeof data.foodPreferences === "string" ? data.foodPreferences.split(",") : data.foodPreferences;
        setDietary(list);
      }
      if (data.favouriteTreats) {
        const list = typeof data.favouriteTreats === "string" ? data.favouriteTreats.split(",") : data.favouriteTreats;
        setFavouriteTreats(list);
      }
      if (data.healthConditions) {
        const list = typeof data.healthConditions === "string" ? data.healthConditions.split(",") : data.healthConditions;
        setHealthConditions(list);
      }

      setDietFrequency(data.dietFrequency || "2");
      setPortionSize(data.portionSize || "1");
      setNoIssues(data.noKnownIssues ?? false);
      setSheddingfrequecy(data.sheddingFrequency || "2");
      setBreedSize((data.breedSize as any) || "Medium");

      if (data.groomingFrequency?.startsWith("Custom:")) {
        setGroomingfrequecy("3");
        const matched = data.groomingFrequency.match(/\d+/);
        if (matched) setCustomGroomingMonths(matched[0]);
      } else {
        setGroomingfrequecy(data.groomingFrequency || "2");
      }
    }
  }, [petResponse]);

  const rawBreeds = breedOptionsMap[selectedType as "dog" | "cat"] || [];
  const filteredBreeds = rawBreeds.filter((b) => b.label.toLowerCase().includes(breedInput.toLowerCase()));
  const filteredColors = colorOptions.filter((c) => c.label.toLowerCase().includes(colorInput.toLowerCase()));

  const toggleChipSelection = (id: string, list: string[], setList: React.Dispatch<React.SetStateAction<string[]>>) => {
    setList(list.includes(id) ? list.filter((item) => item !== id) : [...list, id]);
  };

  const handleAddCustomPersonality = () => {
    const val = customPersonalityInput.trim();
    if (!val) return;
    if (!personality.includes(val)) setPersonality([...personality, val]);
    setCustomPersonalityInput("");
  };

  const handleAddCustomDietary = () => {
    const val = customDietaryInput.trim();
    if (!val) return;
    if (!dietary.includes(val)) setDietary([...dietary, val]);
    setCustomDietaryInput("");
  };

  const handleAddCustomTreat = () => {
    const val = customTreatInput.trim();
    if (!val) return;
    if (!favouriteTreats.includes(val)) setFavouriteTreats([...favouriteTreats, val]);
    setCustomTreatInput("");
  };

  const handleAddCustomHealth = () => {
    const val = healthSearch.trim();
    if (!val) return;
    if (!healthConditions.includes(val)) setHealthConditions([...healthConditions, val]);
    setHealthSearch("");
    if (noIssues) setNoIssues(false);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const response = await uploadFile({ File: file, FolderName: "Customers/Avatars" }).unwrap();
      setPetImage(response.fileUrl);
    } catch {
      showConfirm({ title: "Upload Failed", message: "Image upload failed.", type: "error", confirmText: "OK" });
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload: ICustomerPetRequest = {
        petName,
        petType: selectedType === "dog" ? "Dog" : "Cat",
        breed: breedInput,
        gender: genderInput,
        weight: Number(weight),
        dateType,
        petDate: selectedDate ? new Date(selectedDate).toISOString() : new Date().toISOString(),
        furColor: colorInput,
        activityLevel: activity,
        personality: personality.join(","),
        foodPreferences: dietary.join(","),
        dietFrequency,
        portionSize,
        favouriteTreats: favouriteTreats.join(","),
        noKnownIssues: noIssues,
        healthConditions: healthConditions.join(","),
        requiresVetConsultationAlert: healthConditions.length > 0,
        groomingFrequency: groomingfrequecy === "3" ? `Custom: Every ${customGroomingMonths} Months` : groomingfrequecy,
        sheddingFrequency: sheddingfrequecy,
        breedSize,
        photoUrl: petImage || "",
        isActive: true,
      };

      await updatePet({ userId, petId, data: payload }).unwrap();
      showConfirm({ title: "Success", message: "Pet identity record updated successfully.", type: "success", confirmText: "OK" });
      onSuccess();
    } catch (err: any) {
      showConfirm({ title: "Error", message: err?.data?.Message || "Failed to update pet profile.", type: "error", confirmText: "OK" });
    }
  };

  if (isFetching) return <div className="p-8 text-center text-xs text-iconColor">Fetching pet details...</div>;

  return (
    <div className="bg-[#F4F4F2] p-5 sm:p-6 rounded-3xl space-y-6 text-xs text-left">
      <div className="flex justify-between items-center pb-3 border-b border-gray-200">
        <div>
          <h3 className="text-base font-black text-[#2F5E2E]">Editing Pet Profile Passport ({petName})</h3>
          <p className="text-[11px] text-gray-500 font-medium">Update all attributes directly on a single unified console.</p>
        </div>
      </div>

      <form onSubmit={handleFormSubmit} className="space-y-6">
        {/* SECTION 1: BASIC PET DETAILS */}
        <div className="bg-white p-5 rounded-2xl border border-[#A8D97B]/40 space-y-4 shadow-sm">
          <h4 className="font-bold text-[#2F5E2E] text-sm">1. Basic Identity Details</h4>

          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex flex-col items-center shrink-0">
              <div
                className="w-32 h-32 rounded-2xl bg-[#F4F4F2] border-2 border-[#A8D97B] flex items-center justify-center overflow-hidden cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <img src={petImage || petTypes[0].image} className="w-full h-full object-cover" alt="pet" />
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="mt-2 text-[11px] font-bold text-[#2F5E2E] flex items-center gap-1"
              >
                <Camera size={12} /> Change Avatar
              </button>
            </div>

            <div className="flex-1 space-y-4">
              <div>
                <label className="text-xs font-bold text-[#2F5E2E] block mb-1.5">Select Pet Class Type</label>
                <div className="grid grid-cols-2 gap-2 max-w-xs">
                  {petTypes.map((t) => (
                    <button
                      type="button"
                      key={t.id}
                      onClick={() => setSelectedType(t.label.toLowerCase())}
                      className={`p-2 rounded-xl border-2 flex items-center gap-2 font-bold transition ${
                        selectedType === t.label.toLowerCase()
                          ? "border-[#6DBE45] bg-[#A8D97B]/20 text-[#2F5E2E]"
                          : "border-gray-200 bg-white text-gray-500"
                      }`}
                    >
                      <img src={t.image} className="w-5 h-5 object-contain" alt={t.label} />
                      <span>{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-[#2F5E2E] block mb-1">Pet Name *</label>
                  <input
                    type="text"
                    value={petName}
                    onChange={(e) => setPetName(e.target.value)}
                    className="w-full p-2.5 border border-[#A8D97B] rounded-xl outline-none bg-white font-medium"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-[#2F5E2E] block mb-1">Gender</label>
                  <select
                    value={genderInput}
                    onChange={(e) => setGenderInput(e.target.value)}
                    className="w-full p-2.5 border border-[#A8D97B] rounded-xl outline-none bg-white font-medium"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="relative">
                  <label className="text-xs font-bold text-[#2F5E2E] block mb-1">Breed</label>
                  <input
                    type="text"
                    value={breedInput}
                    onChange={(e) => {
                      setBreedInput(e.target.value);
                      setOpenDropdown("breed");
                    }}
                    onFocus={() => setOpenDropdown("breed")}
                    placeholder="Search or type breed"
                    className="w-full p-2.5 border border-[#A8D97B] rounded-xl outline-none bg-white font-medium"
                  />
                  {openDropdown === "breed" && filteredBreeds.length > 0 && (
                    <div className="absolute top-full left-0 w-full bg-white border rounded-xl shadow-lg max-h-40 overflow-y-auto mt-1 z-20">
                      {filteredBreeds.map((opt) => (
                        <div
                          key={opt.value}
                          onClick={() => {
                            setBreedInput(opt.label);
                            setOpenDropdown(null);
                          }}
                          className="p-2 hover:bg-slate-50 cursor-pointer"
                        >
                          {opt.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="relative">
                  <label className="text-xs font-bold text-[#2F5E2E] block mb-1">Fur Color</label>
                  <input
                    type="text"
                    value={colorInput}
                    onChange={(e) => {
                      setColorInput(e.target.value);
                      setOpenDropdown("color");
                    }}
                    onFocus={() => setOpenDropdown("color")}
                    placeholder="Color"
                    className="w-full p-2.5 border border-[#A8D97B] rounded-xl outline-none bg-white font-medium"
                  />
                  {openDropdown === "color" && filteredColors.length > 0 && (
                    <div className="absolute top-full left-0 w-full bg-white border rounded-xl shadow-lg max-h-40 overflow-y-auto mt-1 z-20">
                      {filteredColors.map((opt) => (
                        <div
                          key={opt.value}
                          onClick={() => {
                            setColorInput(opt.label);
                            setOpenDropdown(null);
                          }}
                          className="p-2 hover:bg-slate-50 cursor-pointer"
                        >
                          {opt.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-xs font-bold text-[#2F5E2E] block mb-1">Weight (KG) *</label>
                  <input
                    type="number"
                    step="0.1"
                    value={weight || ""}
                    onChange={(e) => setWeight(Number(e.target.value))}
                    className="w-full p-2.5 border border-[#A8D97B] rounded-xl outline-none bg-white font-medium"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div>
                  <label className="text-xs font-bold text-[#2F5E2E] block mb-1">Timeline Assignment</label>
                  <div className="flex gap-2 mb-1">
                    <button
                      type="button"
                      onClick={() => setDateType("birthday")}
                      className={`px-3 py-1 rounded-lg text-xs font-bold ${
                        dateType === "birthday" ? "bg-[#2F5E2E] text-white" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      Birthday
                    </button>
                    <button
                      type="button"
                      onClick={() => setDateType("adoption")}
                      className={`px-3 py-1 rounded-lg text-xs font-bold ${
                        dateType === "adoption" ? "bg-[#2F5E2E] text-white" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      Adoption
                    </button>
                  </div>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full p-2.5 border border-[#A8D97B] rounded-xl outline-none bg-white font-medium"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-[#2F5E2E] block mb-1">Personality Traits</label>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {localPersonalities.map((item) => {
                      const isChecked = personality.includes(item.label);
                      return (
                        <button
                          type="button"
                          key={item.id}
                          onClick={() => toggleChipSelection(item.label, personality, setPersonality)}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border flex items-center gap-1 ${
                            isChecked ? "bg-[#2F5E2E] text-white" : "bg-emerald-50 text-emerald-800 border-emerald-200"
                          }`}
                        >
                          <PawPrint size={10} /> {item.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex gap-2 items-center bg-white p-1 rounded-xl border border-emerald-200">
                    <input
                      type="text"
                      placeholder="Add custom personality..."
                      value={customPersonalityInput}
                      onChange={(e) => setCustomPersonalityInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddCustomPersonality())}
                      className="text-xs px-2 py-1 outline-none flex-1 bg-transparent"
                    />
                    <button
                      type="button"
                      onClick={handleAddCustomPersonality}
                      className="bg-[#2F5E2E] text-white rounded-lg p-1"
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 2: FOOD & DIET PREFERENCES */}
        <div className="bg-white p-5 rounded-2xl border border-[#A8D97B]/40 space-y-4 shadow-sm">
          <h4 className="font-bold text-[#2F5E2E] text-sm">2. Food & Dietary Preferences</h4>

          <div>
            <label className="text-xs font-bold text-[#2F5E2E] block mb-2">Dietary Types & Food Styles</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {localDietaryOptions.map((item) => {
                const isChecked = dietary.includes(item.label);
                return (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => toggleChipSelection(item.label, dietary, setDietary)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border flex items-center gap-1 ${
                      isChecked ? "bg-[#2F5E2E] text-white" : "bg-emerald-50 text-emerald-800 border-emerald-200"
                    }`}
                  >
                    <PawPrint size={10} /> {item.label}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2 max-w-xs items-center bg-white p-1 rounded-xl border border-emerald-200">
              <input
                type="text"
                placeholder="Add custom meal preference..."
                value={customDietaryInput}
                onChange={(e) => setCustomDietaryInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddCustomDietary())}
                className="text-xs px-2 py-1 outline-none flex-1 bg-transparent"
              />
              <button
                type="button"
                onClick={handleAddCustomDietary}
                className="bg-[#2F5E2E] text-white rounded-lg p-1"
              >
                <Plus size={12} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-[#2F5E2E] block mb-1">Feeding Frequency</label>
              <select
                value={dietFrequency}
                onChange={(e) => setDietFrequency(e.target.value)}
                className="w-full p-2.5 border border-[#A8D97B] rounded-xl outline-none bg-white font-medium"
              >
                {dietfrequecy.map((df) => (
                  <option key={df.id} value={df.id}>{df.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-[#2F5E2E] block mb-1">Portion Calibration</label>
              <select
                value={portionSize}
                onChange={(e) => setPortionSize(e.target.value)}
                className="w-full p-2.5 border border-[#A8D97B] rounded-xl outline-none bg-white font-medium"
              >
                {protionSize.map((ps) => (
                  <option key={ps.id} value={ps.id}>{ps.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-[#2F5E2E] block mb-2">Favorite Treats & Snacks</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {localTreatOptions.map((item) => {
                const isChecked = favouriteTreats.includes(item.label);
                return (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => toggleChipSelection(item.label, favouriteTreats, setFavouriteTreats)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border flex items-center gap-1 ${
                      isChecked ? "bg-[#2F5E2E] text-white" : "bg-emerald-50 text-emerald-800 border-emerald-200"
                    }`}
                  >
                    <PawPrint size={10} /> {item.label}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2 max-w-xs items-center bg-white p-1 rounded-xl border border-emerald-200">
              <input
                type="text"
                placeholder="Add custom signature snack..."
                value={customTreatInput}
                onChange={(e) => setCustomTreatInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddCustomTreat())}
                className="text-xs px-2 py-1 outline-none flex-1 bg-transparent"
              />
              <button
                type="button"
                onClick={handleAddCustomTreat}
                className="bg-[#2F5E2E] text-white rounded-lg p-1"
              >
                <Plus size={12} />
              </button>
            </div>
          </div>
        </div>

        {/* SECTION 3: HEALTH CONDITIONS */}
        <div className="bg-white p-5 rounded-2xl border border-[#A8D97B]/40 space-y-4 shadow-sm">
          <h4 className="font-bold text-[#2F5E2E] text-sm">3. Health Conditions & Sensitivities</h4>

          <div className="flex gap-2 items-center max-w-md">
            <input
              type="text"
              placeholder="Search or specify custom health symptom..."
              value={healthSearch}
              onChange={(e) => setHealthSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddCustomHealth())}
              className="flex-1 p-2 border border-[#A8D97B] rounded-xl text-xs outline-none"
            />
            <button
              type="button"
              onClick={handleAddCustomHealth}
              className="bg-[#2F5E2E] text-white p-2 rounded-xl"
            >
              <Plus size={14} />
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                setNoIssues(!noIssues);
                if (!noIssues) setHealthConditions([]);
              }}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold border ${
                noIssues ? "bg-emerald-700 text-white" : "bg-white text-gray-700"
              }`}
            >
              No Known Issues
            </button>
            {localHealthConditions.map((item) => {
              const isChecked = healthConditions.includes(item.label);
              return (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => {
                    toggleChipSelection(item.label, healthConditions, setHealthConditions);
                    if (noIssues) setNoIssues(false);
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                    isChecked ? "bg-[#2F5E2E] text-white" : "bg-white text-gray-700 border-gray-200"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* SECTION 4: GROOMING & BREED SIZE */}
        <div className="bg-white p-5 rounded-2xl border border-[#A8D97B]/40 space-y-4 shadow-sm">
          <h4 className="font-bold text-[#2F5E2E] text-sm">4. Grooming & Breed Size Parameters</h4>

          <div>
            <label className="text-xs font-bold text-[#2F5E2E] block mb-2">Breed Size Specification</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              {(["Small", "Medium", "Large", "Extra Large"] as const).map((size) => (
                <button
                  type="button"
                  key={size}
                  onClick={() => setBreedSize(size)}
                  className={`py-2 rounded-xl border text-xs font-bold transition ${
                    breedSize === size ? "bg-[#2F5E2E] text-white" : "bg-white text-gray-600"
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-[#2F5E2E] block mb-1">Grooming Interval</label>
              <select
                value={groomingfrequecy}
                onChange={(e) => setGroomingfrequecy(e.target.value)}
                className="w-full p-2.5 border border-[#A8D97B] rounded-xl outline-none bg-white font-medium"
              >
                {groomingfrequecyOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>

              {groomingfrequecy === "3" && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs font-bold text-gray-600">Custom Months:</span>
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={customGroomingMonths}
                    onChange={(e) => setCustomGroomingMonths(e.target.value)}
                    className="w-16 p-1 border rounded-lg text-xs"
                  />
                </div>
              )}
            </div>

            <div>
              <label className="text-xs font-bold text-[#2F5E2E] block mb-1">Shedding Tendency</label>
              <select
                value={sheddingfrequecy}
                onChange={(e) => setSheddingfrequecy(e.target.value)}
                className="w-full p-2.5 border border-[#A8D97B] rounded-xl outline-none bg-white font-medium"
              >
                {sheddingfrequecyOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* SUBMIT ACTION BUTTON */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="submit"
            disabled={isUpdating}
            className="px-6 py-2.5 bg-[#2F5E2E] hover:bg-[#1f3f1e] text-white font-bold rounded-xl transition shadow-md"
          >
            {isUpdating ? "Saving..." : "Update Pet Profile Configuration"}
          </button>
        </div>
      </form>
    </div>
  );
};

// =========================================================================
// SUB-COMPONENT: FULL ADDRESS EDITOR FORM (WITH MAPS & ALL GET DATA FIELDS)
// =========================================================================

const defaultCenter = { lat: 25.2048, lng: 55.2708 };
const containerStyle = { width: "100%", height: "180px", borderRadius: "12px" };

const AddressEditorForm: React.FC<{
  userId: number;
  addressId: number | null;
  onCancel: () => void;
  onSuccess: () => void;
}> = ({ userId, addressId, onCancel, onSuccess }) => {
  const isEditMode = Boolean(addressId);

  const { isLoaded } = useJsApiLoader({
    id: "admin-google-map-script",
    googleMapsApiKey: MAPS_KEY,
    libraries: ["places"],
  });

  const [fetchAddress, { data: fetchedData, isFetching }] = useLazyCustomerAddressesByIdQuery();
  const [updateAddress, { isLoading: isUpdating }] = useUpdateCustomerAddressesByIdMutation();

  const [selectedCode, setSelectedCode] = useState(countryCodes[0]);
  const [phone, setPhone] = useState("");
  const [isLocating, setIsLocating] = useState(false);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);

  const [formValues, setFormValues] = useState<ICustomerAddressRequest>({
    fullName: "",
    phoneNumber: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    country: "India",
    postalCode: "",
    latitude: defaultCenter.lat,
    longitude: defaultCenter.lng,
    addressType: "Home",
    isDefault: true,
    isActive: true,
  });

  const mapCenterCoordinates = useMemo(
    () => ({
      lat: Number(formValues.latitude) || defaultCenter.lat,
      lng: Number(formValues.longitude) || defaultCenter.lng,
    }),
    [formValues.latitude, formValues.longitude]
  );

  useEffect(() => {
    if (isEditMode && addressId) {
      fetchAddress({ userId, addressId });
    }
  }, [userId, addressId]);

  useEffect(() => {
    if (fetchedData?.data) {
      const addr = fetchedData.data;

      let rawPhone = addr.phoneNumber || "";
      if (rawPhone.startsWith("+971")) {
        setSelectedCode(countryCodes[0]);
        rawPhone = rawPhone.replace("+971", "");
      } else if (rawPhone.startsWith("+91")) {
        setSelectedCode(countryCodes[1]);
        rawPhone = rawPhone.replace("+91", "");
      }

      setPhone(rawPhone);
      setFormValues({
        fullName: addr.fullName || "",
        phoneNumber: addr.phoneNumber || "",
        addressLine1: addr.addressLine1 || "",
        addressLine2: addr.addressLine2 || "",
        city: addr.city || "",
        state: addr.state || "",
        country: addr.country || "India",
        postalCode: addr.postalCode || "",
        latitude: Number(addr.latitude) || defaultCenter.lat,
        longitude: Number(addr.longitude) || defaultCenter.lng,
        addressType: (addr.addressType as any) || "Home",
        isDefault: addr.isDefault || false,
        isActive: addr.isActive ?? true,
      });
    }
  }, [fetchedData]);

  const fetchAddressFromCoords = async (lat: number, lng: number) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${MAPS_KEY}`
      );
      const data = await response.json();

      if (data.status === "OK" && data.results?.[0]) {
        const place = data.results[0];
        const components = place.address_components || [];
        const getComponent = (type: string) =>
          components.find((c: any) => c.types.includes(type))?.long_name || "";

        setFormValues((prev) => ({
          ...prev,
          addressLine1: place.formatted_address || prev.addressLine1,
          city: getComponent("locality") || getComponent("sublocality_level_1") || prev.city,
          state: getComponent("administrative_area_level_1") || prev.state,
          country: getComponent("country") || prev.country,
          postalCode: getComponent("postal_code") || prev.postalCode,
          latitude: lat,
          longitude: lng,
        }));

        if (mapInstance) {
          mapInstance.panTo({ lat, lng });
        }
      }
    } catch (error) {
      console.error("Geocoding failed:", error);
    }
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Location features are not supported by this browser.");
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        await fetchAddressFromCoords(lat, lng);
        setIsLocating(false);
      },
      () => {
        setIsLocating(false);
        alert("Unable to find location.");
      }
    );
  };

  const onMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const clickedLat = e.latLng.lat();
      const clickedLng = e.latLng.lng();
      fetchAddressFromCoords(clickedLat, clickedLng);
    }
  }, []);

  const handlePlaceSelected = (place: any) => {
    if (!place?.address_components) return;
    const components = place.address_components;
    const getComponent = (type: string) =>
      components.find((c: any) => c.types.includes(type))?.long_name || "";

    let lat = formValues.latitude;
    let lng = formValues.longitude;
    if (place.geometry?.location) {
      lat = typeof place.geometry.location.lat === "function" ? place.geometry.location.lat() : place.geometry.location.lat;
      lng = typeof place.geometry.location.lng === "function" ? place.geometry.location.lng() : place.geometry.location.lng;
    }

    setFormValues((prev) => ({
      ...prev,
      addressLine1: place.formatted_address || prev.addressLine1,
      city: getComponent("locality") || getComponent("sublocality_level_1") || prev.city,
      state: getComponent("administrative_area_level_1") || prev.state,
      country: getComponent("country") || prev.country,
      postalCode: getComponent("postal_code") || prev.postalCode,
      latitude: lat,
      longitude: lng,
    }));

    if (mapInstance) mapInstance.panTo({ lat, lng });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addressId) return;

    try {
      const completePhoneString = `${selectedCode.label}${phone.trim()}`;
      await updateAddress({
        userId,
        addressId,
        data: {
          ...formValues,
          phoneNumber: completePhoneString,
        },
      }).unwrap();
      onSuccess();
    } catch {
      alert("Failed to update customer address.");
    }
  };

  if (isFetching) return <div className="p-8 text-center text-xs text-iconColor">Loading address details...</div>;

  return (
    <div className="bg-white p-5 border rounded-2xl space-y-4 text-xs text-left shadow-xs">
      <div className="flex items-center gap-2 border-b pb-3">
        <button onClick={onCancel} className="p-1 border rounded-lg hover:bg-slate-50 transition">
          <ArrowLeft className="w-3.5 h-3.5 text-slate-500" />
        </button>
        <h3 className="font-bold text-[#2F5E2E] text-sm">Modify Address Entry #{addressId}</h3>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Map Preview */}
        <div className="space-y-1">
          <div className="flex justify-between items-center text-[10px] font-bold uppercase text-slate-400">
            <span>Click map to adjust location pin</span>
            <button
              type="button"
              onClick={handleUseCurrentLocation}
              disabled={isLocating}
              className="text-[10px] font-black text-[#2F5E2E] hover:underline flex items-center gap-0.5"
            >
              <MapPin size={10} />
              {isLocating ? "Locating..." : "Use Current Location"}
            </button>
          </div>

          <div className="rounded-xl overflow-hidden border h-40 bg-slate-50 relative">
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={mapCenterCoordinates}
                zoom={14}
                onLoad={(map) => setMapInstance(map)}
                onClick={onMapClick}
                options={{ disableDefaultUI: true, zoomControl: true }}
              >
                <MarkerF position={mapCenterCoordinates} />
              </GoogleMap>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-400">Loading Map...</div>
            )}
          </div>
        </div>

        {/* Search Autocomplete */}
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Search Location *</label>
          <div className="relative flex items-center">
            <MapPin className="absolute left-3 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            <Autocomplete
              apiKey={MAPS_KEY}
              className="w-full h-9 border border-slate-200 bg-slate-50/40 rounded-xl pl-8 pr-3 text-xs font-medium text-slate-800 outline-none focus:border-[#2F5E2E]"
              placeholder="Type address or select from map..."
              onPlaceSelected={handlePlaceSelected}
              defaultValue={formValues.addressLine1}
              key={formValues.addressLine1}
            />
          </div>
        </div>

        {/* Inputs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">Full Name *</label>
            <input
              type="text"
              value={formValues.fullName}
              onChange={(e) => setFormValues({ ...formValues, fullName: e.target.value })}
              className="w-full p-2.5 border border-slate-200 rounded-xl outline-none"
              required
            />
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">Phone Number *</label>
            <div className="flex gap-2">
              <select
                value={selectedCode.value}
                onChange={(e) => {
                  const match = countryCodes.find((c) => c.value === e.target.value);
                  if (match) setSelectedCode(match);
                }}
                className="w-24 p-2 border border-slate-200 rounded-xl outline-none font-bold"
              >
                {countryCodes.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="flex-1 p-2.5 border border-slate-200 rounded-xl outline-none"
                required
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">Street Address Line 1 *</label>
            <input
              type="text"
              value={formValues.addressLine1}
              onChange={(e) => setFormValues({ ...formValues, addressLine1: e.target.value })}
              className="w-full p-2.5 border border-slate-200 rounded-xl outline-none"
              required
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">Flat / Villa / Suite (Line 2)</label>
            <input
              type="text"
              value={formValues.addressLine2 || ""}
              onChange={(e) => setFormValues({ ...formValues, addressLine2: e.target.value })}
              className="w-full p-2.5 border border-slate-200 rounded-xl outline-none"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">City *</label>
            <input
              type="text"
              value={formValues.city}
              onChange={(e) => setFormValues({ ...formValues, city: e.target.value })}
              className="w-full p-2 border border-slate-200 rounded-xl outline-none"
              required
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">State *</label>
            <input
              type="text"
              value={formValues.state}
              onChange={(e) => setFormValues({ ...formValues, state: e.target.value })}
              className="w-full p-2 border border-slate-200 rounded-xl outline-none"
              required
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">Postal Code</label>
            <input
              type="text"
              value={formValues.postalCode}
              onChange={(e) => setFormValues({ ...formValues, postalCode: e.target.value })}
              className="w-full p-2 border border-slate-200 rounded-xl outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">Country</label>
            <input
              type="text"
              value={formValues.country}
              onChange={(e) => setFormValues({ ...formValues, country: e.target.value })}
              className="w-full p-2 border border-slate-200 rounded-xl outline-none"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between border-t pt-3">
          <div>
            <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">Address Label</label>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-1 font-bold text-slate-600 cursor-pointer">
                <input
                  type="radio"
                  name="addressType"
                  checked={formValues.addressType === "Home"}
                  onChange={() => setFormValues({ ...formValues, addressType: "Home" })}
                  className="accent-[#2F5E2E]"
                />
                Home
              </label>
              <label className="flex items-center gap-1 font-bold text-slate-600 cursor-pointer">
                <input
                  type="radio"
                  name="addressType"
                  checked={formValues.addressType !== "Home"}
                  onChange={() => setFormValues({ ...formValues, addressType: "Work" })}
                  className="accent-[#2F5E2E]"
                />
                Work
              </label>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-2 sm:mt-0">
            <input
              type="checkbox"
              id="isDefault"
              checked={formValues.isDefault}
              onChange={(e) => setFormValues({ ...formValues, isDefault: e.target.checked })}
              className="accent-[#2F5E2E]"
            />
            <label htmlFor="isDefault" className="text-xs font-bold text-slate-700 cursor-pointer">Set as Primary Address</label>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <button type="button" onClick={onCancel} className="px-4 py-2 border rounded-xl font-bold text-slate-600 hover:bg-slate-50">
            Cancel
          </button>
          <button type="submit" disabled={isUpdating} className="px-5 py-2 bg-[#2F5E2E] text-white font-bold rounded-xl hover:bg-[#1f3f1e] transition">
            {isUpdating ? "Saving..." : "Update Address"}
          </button>
        </div>
      </form>
    </div>
  );
};